import { FC, useState, useEffect, useMemo } from "react";
import { Select, Switch, Tag } from "antd";
import { Globe, Truck, Hammer } from "lucide-react";
import { useOpenRoutes, useMyRoutes } from "~/services/network";
import { useGetVendors } from "~/services/vendor";
import { useGetShuttles } from "~/services/vessel/vessel.queries";
import { ServiceType } from "~/utils/constants";
import dayjs from "dayjs";

// Map service types to relevant vendor service categories
const serviceTypeToVendorServices: Record<string, string[]> = {
  [ServiceType.COLLECTION]: ["Transportation", "Freight Forwarding", "Fine Art Shipping"],
  [ServiceType.DELIVERY]: ["Transportation", "Freight Forwarding", "Fine Art Shipping"],
  [ServiceType.FABRICATION_SERVICE]: ["Packing & Crating", "Installation Services"],
  [ServiceType.WAREHOUSE_SERVICE]: ["Warehousing", "Storage", "Climate Control"],
  [ServiceType.ON_SITE_SERVICE]: ["Installation Services", "Fine Art Shipping"],
};

interface NetworkConnectProps {
  serviceType: ServiceType;
  networkRouteId?: string;
  vendorId?: string;
  shuttleId?: string;
  onNetworkRouteChange: (id: string | undefined) => void;
  onVendorChange: (id: string | undefined) => void;
  onShuttleChange?: (id: string | undefined) => void;
}

export const NetworkConnect: FC<NetworkConnectProps> = ({
  serviceType,
  networkRouteId,
  vendorId,
  shuttleId,
  onNetworkRouteChange,
  onVendorChange,
  onShuttleChange,
}) => {
  const [enabled, setEnabled] = useState(!!networkRouteId || !!vendorId || !!shuttleId);

  // Sync toggle if values come in from editing
  useEffect(() => {
    if (networkRouteId || vendorId || shuttleId) setEnabled(true);
  }, [networkRouteId, vendorId, shuttleId]);

  const { data: openRoutes = [] } = useOpenRoutes();
  const { data: myRoutes = [] } = useMyRoutes();
  const { data: vendorsData } = useGetVendors({ pageSize: 500 });
  const { data: shuttlesData } = useGetShuttles();

  // Merge own routes + open routes from other orgs, deduplicate
  const allRoutes = useMemo(() => {
    const map = new Map<string, typeof openRoutes[number]>();
    for (const r of myRoutes) map.set(r.id, r);
    for (const r of openRoutes) map.set(r.id, r);
    return Array.from(map.values());
  }, [openRoutes, myRoutes]);

  // Internal shuttles — all available
  const activeShuttles = useMemo(() => {
    return shuttlesData?.items || [];
  }, [shuttlesData]);

  const isTransportService =
    serviceType === ServiceType.COLLECTION || serviceType === ServiceType.DELIVERY;

  // Sort vendors: relevant ones first, then the rest
  const sortedVendors = useMemo(() => {
    const vendors = vendorsData?.items || [];
    const relevantServices = serviceTypeToVendorServices[serviceType] || [];
    if (relevantServices.length === 0) return vendors;
    const relevant: typeof vendors = [];
    const other: typeof vendors = [];
    for (const v of vendors) {
      if (v.services?.some((s) => relevantServices.includes(s))) {
        relevant.push(v);
      } else {
        other.push(v);
      }
    }
    return [...relevant, ...other];
  }, [vendorsData, serviceType]);

  const shuttleOptions = activeShuttles.map((shuttle) => ({
    value: shuttle.id,
    label: `${shuttle.shuttleRef || "Shuttle"} → ${shuttle.destination}`,
    shuttle,
  }));

  const routeOptions = allRoutes.map((route) => ({
    value: route.id,
    label: `${route.origin} → ${route.destination}`,
    route,
  }));

  const vendorOptions = sortedVendors.map((vendor) => ({
    value: vendor.id,
    label: vendor.name,
    vendor,
  }));

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onNetworkRouteChange(undefined);
      onVendorChange(undefined);
      onShuttleChange?.(undefined);
    }
  };

  const clearOthers = (keep: "shuttle" | "route" | "vendor") => {
    if (keep !== "shuttle") onShuttleChange?.(undefined);
    if (keep !== "route") onNetworkRouteChange(undefined);
    if (keep !== "vendor") onVendorChange(undefined);
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-purple-200">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-2">
          <Globe size={16} className="text-purple-600" />
          Network Connect
        </label>
        <Switch
          size="small"
          checked={enabled}
          onChange={handleToggle}
        />
      </div>

      {enabled && (
        <div className="mt-3 space-y-3">
          {/* Internal Shuttles — shown for transport services */}
          {isTransportService && shuttleOptions.length > 0 && (
            <div>
              <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                <Truck size={12} />
                Company Shuttle
              </label>
              <Select
                placeholder="Select an internal shuttle..."
                value={shuttleId}
                onChange={(val) => {
                  onShuttleChange?.(val);
                  if (val) clearOthers("shuttle");
                }}
                allowClear
                onClear={() => onShuttleChange?.(undefined)}
                className="w-full"
                options={shuttleOptions}
                optionRender={(option) => {
                  const shuttle = option.data.shuttle;
                  return (
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {shuttle.shuttleRef || "Shuttle"} → {shuttle.destination}
                      </span>
                      <span className="text-xs text-gray-500">
                        Departs {dayjs(shuttle.departureDate).format("MMM D, YYYY")}
                        {shuttle.arrivalDate && (
                          <> · Arrives {dayjs(shuttle.arrivalDate).format("MMM D")}</>
                        )}
                        {shuttle.status && (
                          <> · <Tag className="text-[10px] m-0 leading-tight" color="blue">{shuttle.status}</Tag></>
                        )}
                      </span>
                    </div>
                  );
                }}
              />
            </div>
          )}

          {/* Network Routes — shown for transport services */}
          {isTransportService && routeOptions.length > 0 && (
            <div>
              <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                <Globe size={12} />
                Network Route
              </label>
              <Select
                placeholder="Select a network route..."
                value={networkRouteId}
                onChange={(val) => {
                  onNetworkRouteChange(val);
                  if (val) clearOthers("route");
                }}
                allowClear
                onClear={() => onNetworkRouteChange(undefined)}
                className="w-full"
                options={routeOptions}
                optionRender={(option) => {
                  const route = option.data.route;
                  return (
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {route.origin} → {route.destination}
                      </span>
                      <span className="text-xs text-gray-500">
                        {dayjs(route.departureDate).format("MMM D, YYYY")}
                        {route.organization?.name && (
                          <> · {route.organization.name}</>
                        )}
                        {route.maxVolume > 0 && (
                          <> · {route.maxVolume} ft³</>
                        )}
                        {route.costPerCube && (
                          <> · ${route.costPerCube}/ft³</>
                        )}
                      </span>
                    </div>
                  );
                }}
              />
            </div>
          )}

          {/* Vendor selection — filtered by service type */}
          <div>
            <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
              <Hammer size={12} />
              Vendor
            </label>
            <Select
              placeholder="Select a vendor..."
              value={vendorId}
              onChange={(val) => {
                onVendorChange(val);
                if (val) clearOthers("vendor");
              }}
              allowClear
              onClear={() => onVendorChange(undefined)}
              className="w-full"
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={vendorOptions}
              optionRender={(option) => {
                const vendor = option.data.vendor;
                return (
                  <div className="flex flex-col">
                    <span className="font-medium">{vendor.name}</span>
                    {vendor.services && vendor.services.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {vendor.services.slice(0, 3).map((s: string) => (
                          <Tag key={s} className="text-[10px] m-0 leading-tight">
                            {s}
                          </Tag>
                        ))}
                        {vendor.services.length > 3 && (
                          <span className="text-[10px] text-gray-400">
                            +{vendor.services.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>

          {/* Selection indicator */}
          {(shuttleId || networkRouteId || vendorId) && (
            <div className="px-2 py-1 bg-purple-50 rounded text-xs text-purple-700">
              {shuttleId
                ? "Linked to company shuttle"
                : networkRouteId
                  ? "Linked to network route"
                  : "Linked to vendor"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
